/*
* Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
* SPDX-License-Identifier: MIT
*
* Licensed under the MIT License. See the LICENSE accompanying this file
* for the specific language governing permissions and limitations under
* the License.
*/

import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { AmplifySignUp } from '@aws-amplify/ui-react';
import { AuthState, Translations, onAuthUIStateChange } from "@aws-amplify/ui-components";
import { I18n } from '@aws-amplify/core';
import * as Sentry from "@sentry/react";
import pushDataLayerEvent from '../../utils/pushDataLayerEvent';

const mapStateToProps = (state) => {
	return {
		lang: state.app.lang,
	}
};

const Register = (props) => {
	const [defaultEmail, setDefaultEmail] = useState("");
	const [defaultFirstName, setDefaultFirstName] = useState("");
	const [defaultLastName, setDefaultLastName] = useState("");
	const trackedSignUpUsers = useRef(new Set());

	useEffect(() => {
		const dictLang = {
			[Translations.SIGN_UP_HAVE_ACCOUNT_TEXT]: "Hai già un account?",
			[Translations.SIGN_IN_TEXT]: "Torna al Login",
			"An account with the given email already exists.": "Hai già un account Tecniche Nuove",
			"Password did not conform with policy: Password not long enough": "La password deve essere lunga almeno 8 caratteri e deve contenere almeno un carattere speciale (es. !, ?, ^)",
			"1 validation error detected: Value at 'password' failed to satisfy constraint: Member must satisfy regular expression pattern: ^[\\S]+.*[\\S]+$": "La password deve essere lunga almeno 8 caratteri e deve contenere almeno un carattere speciale (es. !, ?, ^)"
		};
		I18n.putVocabulariesForLanguage("it", dictLang);
		I18n.putVocabulariesForLanguage("en", dictLang);

		try {
			let queryStringParams = new URLSearchParams(window.location.search);
			let _defaultEmail = queryStringParams.get("defaultEmail");
			setDefaultEmail(_defaultEmail || "");
			let _defaultFirstName = queryStringParams.get("defaultFirstName");
			setDefaultFirstName(_defaultFirstName || "");
			let _defaultLastName = queryStringParams.get("defaultLastName");
			setDefaultLastName(_defaultLastName || "");
		} catch (error) {
			Sentry.captureException(error);
		}
	}, [])

	useEffect(() => {
		const unsubscribe = onAuthUIStateChange((nextAuthState, authData) => {
			if (nextAuthState !== AuthState.ConfirmSignUp) {
				return;
			}

			const email = authData?.username || authData?.signUpAttrs?.attributes?.email || "";
			const trackingKey = email || "signup_without_email";

			if (trackedSignUpUsers.current.has(trackingKey)) {
				return;
			}

			trackedSignUpUsers.current.add(trackingKey);
			pushDataLayerEvent({
				event: "sso_register",
				auth_method: "email_password",
				user_email: email
			});
		});

		return () => {
			unsubscribe && unsubscribe();
		};
	}, []);

	return (
		<AmplifySignUp className="register"
			usernameAlias="email"
			slot="sign-up"
			submitButtonText='Registrati'
			headerText='Crea il tuo account'
			formFields={[
				{
					type: "name",
					label: "Nome *",
					placeholder: "Inserisci il nome",
					required: true,
					value: defaultFirstName,
					inputProps: {
						'data-test': 'sign-in-name-input',
					},
				},
				{
					type: "custom:surname",
					label: "Cognome *",
					placeholder: "Inserisci il cognome",
					required: true,
					value: defaultLastName,
					inputProps: {
						'data-test': 'sign-in-surname-input',
					},
				},
				{
					type: "email",
					label: "Email *",
					placeholder: "Inserisci l'e-mail",
					required: true,
					value: defaultEmail
				},
				{
					type: "password",
					label: "Password *",
					placeholder: "Inserisci la password",
					required: true,
				},
				{
					type: "phone_number",
					required: false,
					dialCode: "+39"
				},
				{
					type: "locale",
					value: props.lang,
					inputProps: {
						type: 'hidden',
					}
				},
				{
					type: "custom:source",
					value: document.referrer ?? "-",
					inputProps: {
						type: 'hidden',
					}
				},
				/**  Here an example of a custom attribute insertion:
				 *
				 * This assume that the custom field customer-type
				 * is defined in file amplify/backend/auth/amplifyIdentityBrokerAuth/amplifyIdentityBrokerAuth-cloudformation-template.yml
				 *
				 *         - Name: customer-type
				 *           Mutable: true
				 *           Required: false
				 *           AttributeDataType: String
				 *
				,{
					label: I18n.get("YOUR_LABEL"),
					type: "custom:customer-type",
					value: "anything",
					required: false,
				}
				*/
			]}>
		</AmplifySignUp>
	);
}

export default connect(mapStateToProps, {})(Register);
